
import { Box, Chip, Fade, Modal, Typography, Button, TextField, Grid, Select, MenuItem, FormControl, InputLabel } from '@material-ui/core';
import { List as MUIList, ListItem, ListItemText, Paper, Alert } from "@mui/material";
import { getRequestOptions, notifyError, notifySuccess, request } from "../../util";
import { useEffect, useState } from 'react';
import { translate } from '../../i18n/customI18nProvider';
import config from '../../config';
import { getRequestHeaders } from '../../services/common.service';
import axios from 'axios';

/**
 * @component JobStatus
 * * @description
 * An administrative interface component designed to manage persistent server-side jobs 
 * (e.g., background workers, cron tasks, or payment sync services like MyPOS).
 * It provides real-time monitoring of job status, log inspection, and lifecycle control.
 * * @param {string} resourceUrl - The base endpoint for the job (e.g., "/v1/mypos_direct_transactions").
 * All API requests are dynamically constructed using this base path.
 * * ---
 * ### Backend API Requirements
 * The backend service associated with the `resourceUrl` must implement the following routes:
 * * | Endpoint | Method | Description |
 * | :--- | :--- | :--- |
 * | `${resourceUrl}/job_status` | GET | Returns current state (e.g., { "status": "running" | ... }) |
 * | `${resourceUrl}/lastlogs`   | GET | Fetches the most recent 100 log entries |
 * | `${resourceUrl}/logs`       | GET | Streams or returns the full log file |
 * | `${resourceUrl}/start`      | POST | Triggers the service execution |
 * | `${resourceUrl}/stop`       | POST | Gracefully shuts down the service |
 * * @example
 * <JobManager resourceUrl="/v1/mypos_direct_transactions" />
 */
export function JobStatus({resourceUrl}) {
    const REFRESH_INTERVAL = 5; // Refresh logs every 5 seconds when the modal is open

    const [jobStatus, setJobStatus] = useState('undefined');

    const [jobPid, setJobPid] = useState(null);

    const [openLogs, setOpenLogs] = useState(false);

    const [jobLogs, setJobLogs] = useState([]);

    const [alert, setAlert] = useState({show: false, message: ""});

    const [refreshInterval, setRefreshInterval] = useState(REFRESH_INTERVAL);

    const [freezeLogs, setFreezeLogs] = useState(false);

    const [settings, setSettings] = useState({open: false, data: {fetchDelay: 0, executionMode: 'sync'}});

    const isSmall = true;

    useEffect(() => {
        fetchJobStatus();
        fetchSettings();
    }, []);
    
    useEffect(() => {
        if (!openLogs || freezeLogs) return;

        const intervalValue = refreshInterval >= 1 ? refreshInterval : REFRESH_INTERVAL;
        const interval = setInterval(() => {
            fetchJobLogs();
        }, Number(intervalValue * 1000));

        return () => clearInterval(interval);

    }, [openLogs, freezeLogs, refreshInterval]);

    const statusChip = () => {
        if(jobStatus === 'undefined') {
            return <Chip label="Status: undefined" size="small" style={{ backgroundColor: '#9e9e9e', color: '#fff' }} />;
        }
        if(jobStatus === 'running') {
            return <Chip label="Status: running" size="small" style={{ backgroundColor: '#1976d2', color: '#fff' }} />;
        }
        if(jobStatus === 'stopped') {
            return <Chip label="Status: stopped" size="small" style={{ backgroundColor: '#ff4400ff', color: '#fff' }} />;
        }
        return jobStatus;
    }

    const levelColors = {
        info: "#d1ecf1",
        warn: "#fff3cd",
        error: "#f8d7da",
        unknown: "#fff"
    };

    const validLogsFormat = (logs) => {
        if(!Array.isArray(logs)){
            // Logs has to be an array
            return false;
        }
        for(const log of logs) {
            if(log.timestamp && log.level && (log.message || log.error)){
                // If at least one log has the correct format we consider that the logs format is correct, even if some logs are not correctly formatted
                return true;
            }
        }
        return false;
    }

    const fetchJobLogs = async () => {
        try {
            const jobLogs = await request( `${resourceUrl}/lastlogs`, 'GET', null);
            if(validLogsFormat(jobLogs)){
                setJobLogs(jobLogs);
            } else {
                setOpenLogs(false);
                let message = translate('custom.error_fetching_job_logs');
                if(jobLogs.message) message = translate('custom.' + jobLogs.message);
                setAlert({show: true, message: message});
            }            
        } catch (error) {
            console.log(error);
        }
    }

    const showJobLogs = async () => {
        setOpenLogs(true);
        fetchJobLogs();
    }

    const fetchJobStatus = async () => {
        const jobStatus = await request(`${resourceUrl}/job_status`, 'GET', null);
        if(jobStatus.status){
            setJobStatus(jobStatus.status);
            if(jobStatus.status === 'running'){
                setJobPid(jobStatus.pid);
            } else {
                setJobPid(null);
            }
        } else {
            setAlert({show: true, message: translate('custom.error_fetching_job_status')})
        }
    };

    const fetchSettings = async () => {
        const response = await request(`${resourceUrl}/settings`, 'GET', null);
        if(response) {
            setSettings({...settings, data: response});
        }
    };

    async function downloadFileFromResponse(response, file_name, axios=false) {
        let blob;
        if(axios) {
            blob = await response.data;
        } else {
            blob = await response.blob();
        }
        const newBlob = new Blob([blob]);
        const blobUrl = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', file_name);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blob);
    }

    const downloadJobLogs = async () => {
        const response = await fetch(config.apiUrl + `${resourceUrl}/logs`, getRequestOptions('GET'));
        await downloadFileFromResponse(response, 'logs.txt');
    }

    const hideJobLogs = () => {
        setOpenLogs(false);
        setJobLogs([]);
    }
    
    function formatDate(isoDate) {
        if (!isoDate) return '';
        const normalizedIsoDate = isoDate.replace(' ', 'T').replace(' ', '');
        const date = new Date(normalizedIsoDate);

        if (isNaN(date.getTime())) return '';

        return new Intl.DateTimeFormat("fr-FR", {
            year: "2-digit",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }).format(date);
    }

    const changeRefreshInterval = (newInterval) => {
        if(newInterval >= 1 || newInterval === "" || newInterval == null) {
            setRefreshInterval(newInterval);
        }
    }

    const toogleJob = async () => {
        if(jobStatus === "running") {
            //Stop PWL Job
            const response = await fetch(config.apiUrl + `${resourceUrl}/stop`, getRequestOptions('POST'));
            if(response.status == 200) {
                setTimeout(() => {
                    fetchJobStatus()
                }, 1000);
            }
        } else {
            //Start PWL Job
            const response = await fetch(config.apiUrl + `${resourceUrl}/start`, getRequestOptions('POST'));
            if(response.status == 200) {
                setTimeout(() => {
                    fetchJobStatus()
                }, 1000);
            }
        }
    }

    const restartJob = async () => {
        if(jobStatus == "running") {
            const response = await fetch(config.apiUrl + `${resourceUrl}/stop`, getRequestOptions('POST'));
            if(response.status == 200) {
                setTimeout(async () => {
                    fetchJobStatus();
                    setTimeout(async () => {
                        const response = await fetch(config.apiUrl + `${resourceUrl}/start`, getRequestOptions('POST'));
                        if(response.status == 200) {
                            setTimeout(() => {
                                fetchJobStatus()
                            }, 1000);
                        }
                    }, 1000);
                }, 1000);
            }
        } else {
            toogleJob();
        }
    }

    const handleConfig = () => {
        setSettings({...settings, open: true });
    }

    const hideConfig = () => {
        setSettings({...settings, open: false });
    }

    const handleChangeSettings = (key, value) => {
        setSettings({...settings, data: {...settings.data, [key]: value}});
    }

    const handleSaveSettings = async (restart) => {
        try {
            let headers = getRequestHeaders();
            const response = await axios.post(config.apiUrl + `${resourceUrl}/settings`, settings.data, { headers: headers });

            if(!response.status || response.status !== 200) {
                notifyError(response?.error);
                return;
            }

            notifySuccess('custom.config_saved');
            hideConfig();
            if(restart) {
                await restartJob();
            }
        } catch (error) {
            notifyError(error?.response?.data?.error);
        }
    }
    

    return (
        <>
        <div>
            {
                isSmall ? (
                    <>
                        <div style={{margin: 10, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 10}}>
                            {statusChip()}
                            {jobPid && <Chip label={`PID: ${jobPid}`} size="small" style={{ backgroundColor: '#1976d2', color: '#fff' }} />}
                        </div>
                        <div style={{margin: 10, display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 10}}>
                            <Button onClick={toogleJob} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white'}}>{jobStatus == "running" ? translate('custom.stop_process') : translate('custom.start_process')}</Button>
                            <Button onClick={handleConfig} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white'}}>{translate('custom.config')}</Button>
                            <Button onClick={showJobLogs} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white'}}>{translate('custom.show_100_logs')}</Button>
                            <Button onClick={downloadJobLogs} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white'}}>{translate('custom.download_logs_file')}</Button>
                        </div>
                    </>
                ) : (<>
                    <span>{statusChip()} {jobPid && <Chip label={`PID: ${jobPid}`} size="small" style={{ backgroundColor: '#1976d2', color: '#fff' }} />}</span>
                    <Button onClick={toogleJob} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white', margin: 10}}>{jobStatus == "running" ? translate('custom.stop_process') : translate('custom.start_process')}</Button>
                    <Button onClick={handleConfig} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white', margin: 10}}>{translate('custom.config')}</Button>
                    <Button onClick={showJobLogs} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white', margin: 10}}>{translate('custom.show_100_logs')}</Button>
                    <Button onClick={downloadJobLogs} size="small" variant="contained" style={{backgroundColor: config.baseColor, color: 'white', margin: 10}}>{translate('custom.download_logs_file')}</Button>
                </>)
            }
            <Modal
                open={openLogs}
                onClose={hideJobLogs}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
                style={{margin: "auto", maxWidth: "800px"}}
            >
                <Box sx={{ maxHeight: "90vh", marginTop: "5vh", marginBottom: "5vh"}}>
                    <Fade in={openLogs}>
                        <MUIList sx={{ width: "100%", maxWidth: "800px", margin: "auto" }}>
                            <div style={{backgroundColor: 'white', padding: 10, borderRadius: "10px", overflowY: "auto", maxHeight: "90vh"}}>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button color="primary" style={{height: '100%'}} variant="contained" fullWidth onClick={() => setFreezeLogs(!freezeLogs)}>{freezeLogs ? translate('custom.start_refresh') : translate('custom.stop_refresh')}</Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField type="number" fullWidth variant="standard" size="small" value={refreshInterval} onChange={(e) => changeRefreshInterval(e.target.value)} label={translate('custom.refresh_interval')} />
                                    </Grid>
                                </Grid>
                                <p variant="body1" style={{textAlign: "center"}}>
                                    {translate('custom.last_100_logs')}
                                </p>
                                {jobLogs.slice().reverse().map((log, index) => (
                                    <Paper
                                        key={index}
                                        sx={{
                                            backgroundColor: levelColors[log.level] || "#fff",
                                            marginBottom: 1,
                                            padding: 0,
                                        }}
                                        elevation={1}
                                    >
                                    <ListItem>
                                        <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight="bold">
                                                {log.message ? log.message : log.error}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="secondary">
                                                {formatDate(log.timestamp)}
                                            </Typography>
                                        }
                                        />
                                    </ListItem>
                                    </Paper>
                                ))}
                            </div>
                        </MUIList>
                    </Fade>
                </Box>
            </Modal>
            <Modal 
                open={settings.open}
                onClose={hideConfig}
                style={{margin: "auto", maxWidth: "800px"}}
            >
                <Box sx={{ maxHeight: "90vh", marginTop: "5vh", marginBottom: "5vh"}}>
                    <Fade in={settings.open}>
                        <Paper style={{backgroundColor: 'white', padding: 10, borderRadius: "10px", overflowY: "auto", maxHeight: "90vh"}}>
                            <Typography variant="h6" gutterBottom>{translate('custom.config_job')}</Typography>
                            <Typography variant="caption" gutterBottom>{translate('custom.config_job_description')}</Typography>
                            <Grid container spacing={2} style={{marginTop: "20px"}}>
                                <Grid item xs={12}>
                                    <TextField type="number" label={translate('custom.fetch_delay')} fullWidth variant="outlined" size="small" value={settings.data.fetchDelay} onChange={(e) => handleChangeSettings('fetchDelay', e.target.value)} />
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth>
                                        <InputLabel id="execution-mode-label">{translate('custom.execution_mode')}</InputLabel>
                                        <Select
                                            labelId="execution-mode-label"
                                            value={settings.data.executionMode}
                                            onChange={(e) => handleChangeSettings('executionMode', e.target.value)}
                                        >
                                            <MenuItem value={"sync"}>{translate('custom.sync')}</MenuItem>
                                            <MenuItem value={"async"}>{translate('custom.async')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button color="primary" variant="contained" fullWidth onClick={() => handleSaveSettings(false)}>{translate('custom.save')}</Button>
                                </Grid>
                                <Grid item xs={6}>
                                    <Button color="primary" variant="contained" fullWidth onClick={() => handleSaveSettings(true)}>{translate('custom.save_and_restart')}</Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>
                </Box>
            </Modal>
        </div>
        <div style={{display: !alert.show ? "none" : "block", position: "fixed", top: 60, right: 20, zIndex: 2000}}>
            <Alert variant="filled" severity="error" color="warning" onClose={() => {setAlert({show: false, message: ""})}}>
                {alert.message}
            </Alert>
        </div>
        </>
    )
}